import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { EditInDialogButtonProps, FormDialogContext } from '@react-admin/ra-form-layout';
import { useCallback } from 'react';
import {
  RaRecord,
  required,
  TextInput,
  TransformData,
  useGetOne,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from 'react-admin';

import {
  ActionModalFormWithToolbar,
  DialogToolbar,
  EditInDialogButton,
  SaveButton,
} from '@sh4res/bo-common/components';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useCanAccess } from '@sh4res/bo-common/hooks';
import { getErrorMessage, inputDisabledProps } from '@sh4res/bo-common/utils';

type RenameDocumentProps<T> = {
  nameSource: keyof T;
  documentUrlSource: keyof T;
  testId?: string;
  isDateOfExpiryRequired?: (record: T) => boolean;
} & EditInDialogButtonProps['ButtonProps'];

const transformData: TransformData = <
  T extends RaRecord & { filename: string; extension: string },
>({
  filename,
  extension,
  ...data
}: T) => ({
  ...data,
  name: [filename.trim(), extension.trim()].join('.'),
});

export const UpdateGenericDocument = <T extends RaRecord & { is_generated: boolean }>({
  nameSource,
  documentUrlSource,
  testId,
  isDateOfExpiryRequired,
  ...btnProps
}: RenameDocumentProps<T>) => {
  const record = useRecordContext<T>();
  const resource = useResourceContext();

  const canAccessTasks = useCanAccess({
    resource: 'tasks',
    action: 'show',
  });
  const { data: task } = useGetOne(
    'tasks',
    { id: record?.task_id as number },
    { enabled: canAccessTasks && G.isNotNullable(record?.task_id) },
  );

  const notify = useNotify();
  const refresh = useRefresh();

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.all_documents.actions.edit.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(() => {
    notify('resources.bo_common.all_documents.actions.edit.success', { type: 'success' });
    refresh();
  }, [notify, refresh]);

  if (!record) {
    return;
  }

  const splittedUrl = record[documentUrlSource]?.replace(/\?[^?]*$/, '')?.split('.');
  const urlExtension = splittedUrl?.at(-1);

  const splittedName = record[nameSource]?.split('.');
  const filename = splittedName?.slice(0, -1).join('.');
  const extension = splittedName?.at(-1) || urlExtension;
  const formRecord = {
    filename: !filename && extension ? extension : filename,
    extension: !filename && extension ? urlExtension || '' : extension,
    date_of_expiry: record.date_of_expiry,
  };

  const canEdit = !record.is_generated;
  const dateOfExpiryRequired = isDateOfExpiryRequired?.(record) ?? false;
  const showDateOfExpiry =
    dateOfExpiryRequired ||
    [
      'b2b_source_of_funds',
      'b2b_withdrawal',
      'b2b_proof_identity',
      'mdlr_bank_name_failed',
    ].includes(task?.type);

  return (
    <EditInDialogButton
      testId={testId ? `${testId}_EditButton` : undefined}
      resource={resource}
      fullWidth
      maxWidth="sm"
      title="resources.bo_common.all_documents.edit_modal.title"
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
          textTransform: 'capitalize',
        },
        disabled: !canEdit,
        ...btnProps,
      }}
      mutationMode="pessimistic"
      transform={transformData}
      mutationOptions={{ onSuccess, onError }}>
      <ActionModalFormWithToolbar
        record={formRecord}
        toolbar={
          <DialogToolbar
            formDialogContext={FormDialogContext}
            button={
              <SaveButton
                color="primary"
                permissions={{
                  resource,
                  action: 'edit',
                }}
                testId={testId ? `${testId}_SaveButton` : undefined}
              />
            }
          />
        }>
        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          <TextInput
            source="filename"
            sx={{ flex: 6 }}
            validate={[(value, values) => required()(`${value}`.trim(), values)]}
            isRequired
            data-testid={testId ? `${testId}_FilenameInput` : undefined}
          />
          <TextInput
            source="extension"
            readOnly={!!formRecord.extension}
            validate={[(value, values) => required()(`${value}`.trim(), values)]}
            isRequired
            data-testid={testId ? `${testId}_ExtensionInput` : undefined}
            {...inputDisabledProps(!!formRecord.extension, { flex: 1, marginLeft: '4px' })}
          />
        </Box>
        {showDateOfExpiry && (
          <DateInput
            source="date_of_expiry"
            validate={dateOfExpiryRequired ? required() : undefined}
            data-testid={testId ? `${testId}_DateOfExpiryInput` : undefined}
          />
        )}
      </ActionModalFormWithToolbar>
    </EditInDialogButton>
  );
};
